import { createContext, useContext } from 'react';

export interface FieldContextValue {
  /** id for the control; the label's `htmlFor` points at it. */
  controlId: string;
  /** Space-separated ids of the help and error text. */
  describedBy?: string;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

interface ControlA11yProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  required?: boolean;
  disabled?: boolean;
}

/**
 * Merge a control's own accessibility props with those of an enclosing
 * `Field`. Explicit props win, except that descriptions are combined.
 */
export const useFieldControl = <P extends ControlA11yProps>(props: P): P => {
  const field = useContext(FieldContext);
  if (!field) return props;
  const describedBy =
    [props['aria-describedby'], field.describedBy].filter(Boolean).join(' ') ||
    undefined;
  return {
    ...props,
    id: props.id ?? field.controlId,
    'aria-describedby': describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    required: props.required ?? (field.required || undefined),
    disabled: props.disabled ?? (field.disabled || undefined),
  };
};
